/**
 * Section « À valider » : une carte par pièce lue et proposée, avec l'aperçu
 * du fichier, sa provenance, les champs lus (en orange quand ils sont
 * douteux), la rubrique et le nom proposés. Valider et déposer, corriger,
 * écarter avec un motif.
 */

import { h } from '../../dom.js';
import {
  corrigerPiece,
  deposerPiece,
  ecarterPiece,
  ErreurPiece,
  type NatureEcartee,
  type NaturePiece,
  type Piece,
  type PieceListee,
} from './api.js';
import { apercuPiece, brut, creerRetour, lienSur, messageDe, resumeProvenance, verrouille, type Contexte } from './communs.js';
import { sectionDeCartes, type ListeCartes, type SectionCartes } from './liste-cartes.js';
import {
  correctionDepuis,
  infobulleChamp,
  messageDepotRefuse,
  MOTIFS_PAR_DEFAUT,
  NATURES,
  type SaisiePiece,
} from './pieces-textes.js';

type Controle = HTMLButtonElement | HTMLInputElement | HTMLSelectElement;

/** Un champ du formulaire d'une pièce : orange et annoncé « à vérifier » quand la lecture est douteuse, provenance en infobulle. */
function champPiece(libelle: string, controle: Controle, douteux: boolean, infobulle: string): HTMLElement {
  controle.title = infobulle;
  return h(
    'label',
    { classe: douteux ? 'champ-piece douteux' : 'champ-piece', title: infobulle },
    libelle,
    douteux ? h('span', { classe: 'invisible' }, ' (lecture douteuse, à vérifier)') : null,
    controle,
  );
}

/** Formulaire d'une pièce : sa valeur initiale, ses contrôles, et la saisie courante. */
function formulairePiece(p: Piece, ctx: Contexte) {
  const c = p.champs;
  const texte = (valeur: string | null | undefined, max = 200): HTMLInputElement =>
    h('input', { type: 'text', maxlength: max, value: valeur ?? '' });

  const nature = h('select', {}, ...(Object.keys(NATURES) as NaturePiece[]).map((n) => h('option', { value: n }, NATURES[n])));
  nature.value = p.nature;
  const fournisseur = texte(c?.fournisseur.valeur);
  const date = h('input', { type: 'date', value: c?.date.valeur ?? '' });
  const montantLu = c?.montant.valeur ?? null;
  const montant = h('input', { type: 'text', inputmode: 'decimal', value: montantLu === null ? '' : montantLu.toFixed(2).replace('.', ',') });
  const devise = h('input', { type: 'text', maxlength: 3, value: c?.devise.valeur ?? '' });
  const numero = texte(c?.numero.valeur, 100);
  const rubrique = h('select', {}, h('option', { value: '' }, 'Aucune : à choisir'));
  if (p.rubrique !== null) rubrique.appendChild(h('option', { value: p.rubrique }, `Rubrique ${p.rubrique}`));
  rubrique.value = p.rubrique ?? '';
  const nom = texte(p.nomPropose, 150);
  nom.classList.add('champ-nom');

  // Les rubriques du Drive arrivent après la carte : elles remplacent l'option provisoire.
  void ctx
    .rubriques(p)
    .then((liste) => {
      const choix = rubrique.value;
      while (rubrique.options.length > 1) rubrique.remove(1);
      for (const r of liste) rubrique.appendChild(h('option', { value: r.numero }, brut(`${r.numero}. ${r.libelle}`)));
      if (choix !== '' && !liste.some((r) => r.numero === choix)) rubrique.appendChild(h('option', { value: choix }, `Rubrique ${choix}`));
      rubrique.value = choix;
    })
    .catch(() => {
      /* La liste reste celle de la rubrique proposée : le serveur refusera une rubrique inconnue. */
    });

  const el = h(
    'div',
    { classe: 'champs-piece' },
    champPiece('Nature', nature, false, 'Ce que la pièce est. Une nature autre que « Facture du SCC » ne permet pas le dépôt.'),
    champPiece('Fournisseur', fournisseur, c?.fournisseur.douteux ?? false, infobulleChamp(c?.fournisseur)),
    champPiece('Date de la facture', date, c?.date.douteux ?? false, infobulleChamp(c?.date)),
    champPiece('Montant TTC', montant, c?.montant.douteux ?? false, infobulleChamp(c?.montant)),
    champPiece('Devise', devise, c?.devise.douteux ?? false, infobulleChamp(c?.devise)),
    champPiece('Numéro', numero, c?.numero.douteux ?? false, infobulleChamp(c?.numero)),
    champPiece('Rubrique du Drive', rubrique, false, p.rubrique === null ? 'Aucune rubrique proposée : à choisir.' : 'Rubrique proposée.'),
    champPiece('Nom du fichier dans le Drive', nom, false, 'Nom proposé : AAAAMMJJ_Fournisseur_Numero.'),
  );
  const controles: Controle[] = [nature, fournisseur, date, montant, devise, numero, rubrique, nom];
  const saisie = (): SaisiePiece => ({
    nature: nature.value as NaturePiece,
    fournisseur: fournisseur.value,
    date: date.value,
    montant: montant.value,
    devise: devise.value,
    numero: numero.value,
    rubrique: rubrique.value,
    nom: nom.value,
  });
  return { el, controles, saisie };
}

const NATURES_ECART: readonly NatureEcartee[] = ['autre_structure', 'facture_emise', 'pas_une_facture'];

/** Carte d'une pièce à valider. `message` : le retour à montrer d'emblée (corrections enregistrées). */
function carteValider(initiale: PieceListee, liste: ListeCartes, ctx: Contexte, message?: string): HTMLElement {
  let courante: PieceListee = initiale;
  const form = formulairePiece(courante, ctx);
  const retour = creerRetour();
  if (message !== undefined) retour.ok(message);

  const valider = h('button', { type: 'button', classe: 'btn-action principal' }, 'Valider et déposer');
  const corriger = h('button', { type: 'button', classe: 'btn-action' }, 'Enregistrer les corrections');
  const ecarter = h('button', { type: 'button', classe: 'btn-action' }, 'Écarter…');

  // Écart : la nature (facultative) et le motif (obligatoire).
  const natureEcart = h(
    'select',
    {},
    h('option', { value: '' }, 'Sans préciser la nature'),
    ...NATURES_ECART.map((n) => h('option', { value: n }, NATURES[n])),
  );
  const motif = h('input', { type: 'text', maxlength: 300, placeholder: 'Pourquoi cette pièce n’est pas à classer', classe: 'champ-motif' });
  const confirmer = h('button', { type: 'button', classe: 'btn-action' }, 'Confirmer l’écart');
  const annuler = h('button', { type: 'button', classe: 'btn-action' }, 'Annuler');
  const formEcart = h(
    'div',
    { classe: 'ecart-piece', hidden: true },
    h('label', {}, 'Nature ', natureEcart),
    h('label', {}, 'Motif ', motif),
    confirmer,
    annuler,
  );
  natureEcart.addEventListener('change', () => {
    const n = natureEcart.value as NatureEcartee | '';
    if (n !== '' && motif.value.trim() === '') motif.value = MOTIFS_PAR_DEFAUT[n];
  });

  const tous = [...form.controles, valider, corriger, ecarter, natureEcart, motif, confirmer, annuler];

  /** Enregistre ce qui a changé ; rend la pièce à jour, ou `null` après avoir signalé le refus. */
  const enregistrer = async (): Promise<Piece | null> => {
    const r = correctionDepuis(courante, form.saisie());
    if ('erreur' in r) {
      retour.ko(r.erreur);
      return null;
    }
    if (Object.keys(r.correction).length === 0) return courante;
    const { piece } = await corrigerPiece(courante.id, r.correction);
    courante = { ...courante, ...piece };
    return piece;
  };

  const carte = h(
    'article',
    { classe: 'carte-piece', 'aria-label': `Pièce ${String(courante.id)}` },
    h('div', { classe: 'carte-apercu' }, apercuPiece(courante)),
    h(
      'div',
      { classe: 'carte-corps' },
      resumeProvenance(courante.provenance, Math.max(0, courante.nbProvenances - 1)),
      courante.motif === null ? null : h('p', { classe: 'note-lecture' }, brut(`Remarque : ${courante.motif}`)),
      form.el,
      h('div', { classe: 'barre-actions' }, valider, corriger, ecarter),
      formEcart,
      retour.el,
    ),
  );

  corriger.addEventListener('click', () => {
    void (async () => {
      try {
        retour.effacer();
        const avant = courante;
        const piece = await verrouille(tous, enregistrer);
        if (piece === null) return;
        if (piece === avant) {
          retour.ok('Rien n’a changé.');
          return;
        }
        liste.remplacer(courante.id, carteValider(courante, liste, ctx, 'Corrections enregistrées.'));
      } catch (e) {
        retour.ko(messageDe(e, 'Les corrections n’ont pas pu être enregistrées.'));
      }
    })();
  });

  valider.addEventListener('click', () => {
    void (async () => {
      try {
        retour.effacer();
        const depot = await verrouille(tous, async () => {
          if ((await enregistrer()) === null) return null;
          return deposerPiece(courante.id);
        });
        if (depot === null) return;
        liste.retirer(courante.id);
        ctx.retour.ok(
          depot.dejaPresent ? 'Ce fichier était déjà dans le Drive : la pièce y est rattachée. ' : 'Pièce déposée dans le Drive : ',
          brut(depot.nom),
          ' ',
          lienSur(depot.lien, 'Ouvrir ↗', 'Ouvrir le fichier dans le Drive'),
        );
        ctx.recharger('historique');
      } catch (e) {
        if (e instanceof ErreurPiece) {
          retour.ko(messageDepotRefuse(e.motif, e.message));
          // La pièce a changé d'état entre-temps : la liste se met à jour.
          if (e.statut === 409) ctx.recharger('valider', 'arbitrer', 'historique');
        } else {
          retour.ko('Le dépôt n’a pas abouti. Réessayez.');
        }
      }
    })();
  });

  ecarter.addEventListener('click', () => {
    formEcart.hidden = false;
    motif.focus();
  });
  annuler.addEventListener('click', () => {
    formEcart.hidden = true;
  });
  confirmer.addEventListener('click', () => {
    const m = motif.value.trim();
    if (m === '') {
      retour.ko('Le motif est obligatoire : il reste dans l’historique.');
      return;
    }
    void (async () => {
      try {
        retour.effacer();
        const n = natureEcart.value as NatureEcartee | '';
        await verrouille(tous, () => ecarterPiece(courante.id, m, n === '' ? undefined : n));
        liste.retirer(courante.id);
        ctx.retour.ok('Pièce écartée. Elle reste dans l’historique : on peut la reprendre.');
        ctx.recharger('historique');
      } catch (e) {
        retour.ko(messageDe(e, 'La pièce n’a pas pu être écartée.'));
      }
    })();
  });

  return carte;
}

export function sectionValider(ctx: Contexte): SectionCartes {
  return sectionDeCartes(
    1,
    'À valider',
    'Lues et proposées : vérifiez, corrigez si besoin, puis déposez dans le Drive',
    'a_valider',
    (p, liste) => carteValider(p, liste, ctx),
    'Aucune pièce à valider.',
    h('p', { classe: 'note-lecture' }, 'Les champs en orange ont été lus avec un doute. Survolez un champ pour savoir d’où vient sa valeur.'),
  );
}
